import React from 'react';
import { Helmet } from 'react-helmet-async';

import { setMapboxAccessToken } from './loadMapbox';

export { MAPBOX_SCRIPT_ID } from './loadMapbox';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Include scripts (like Map Provider).
 *
 * Mapbox: nothing is put into <head> any more. mapbox-gl.js (+ CSS) used to ship on every page
 * for the Topbar location search and the maps, although no page shows a map above the fold on
 * mobile — it was the biggest unused script in Lighthouse. Now:
 *  - maps await `loadMapboxGl()` (util/loadMapbox.js) right before they create a map,
 *  - location search (GeocoderMapbox) calls the Geocoding HTTP API with the access token only.
 * This component just registers the configured access token for both. It does so during render
 * (not in an effect) because class components create their map in componentDidMount, which runs
 * before any useEffect of the same commit.
 *
 * Google Maps is still loaded immediately — its initialization does not support async loading.
 *
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { maps } = props?.config || {};
  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  if (isMapboxInUse) {
    setMapboxAccessToken(mapboxAccessToken);
  }

  if (!isGoogleMapsInUse) {
    return null;
  }

  // GA4 and Plausible: loaded on the client after window load + idle — see DeferredAnalyticsScripts in app.js
  return (
    <Helmet>
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin="anonymous"
      ></script>
    </Helmet>
  );
};
